import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useChat,
  makeHeaderStream,
  AssistantContent,
  ClarifyCard,
  Composer,
  DownloadCard,
  ThinkingBlock,
  useOutsidePress,
} from '@netwatchai/chat-core'
import type { ChatMessage } from '@netwatchai/chat-core'
import { useConfidence } from './useConfidence'
import { useFaq } from './useFaq'
import { useSalutation } from './useSalutation'
import {
  FaqBar,
  FaqPanel,
  Outlook,
  Salutation,
  pinnedEntries,
  type ActiveProblem,
} from '@netwatchai/chat-core'
import { ThinkingLabel } from './ThinkingLabel'
import { INSIGHT_ASK_ENABLED } from './config'
import { ConfirmDialog } from './ConfirmDialog'

function IconLogo() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round">
      <polygon points="12,2.6 20.1,7.3 20.1,16.7 12,21.4 3.9,16.7 3.9,7.3" />
      <polygon points="12,7.1 16.2,9.55 16.2,14.45 12,16.9 7.8,14.45 7.8,9.55" />
      <circle cx="12" cy="12" r="1.9" fill="currentColor" stroke="none" />
    </svg>
  )
}

function IconChevronDown() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

function IconClose() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function IconResize() {
  return (
    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <line x1="11" y1="1" x2="1" y2="11" />
      <line x1="11" y1="5" x2="5" y2="11" />
      <line x1="11" y1="9" x2="9" y2="11" />
    </svg>
  )
}

function IconHistory() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <polyline points="3 3 3 8 8 8" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  )
}

function IconPlus() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

function IconExternal() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6M10 14L21 3" />
    </svg>
  )
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`
  return new Date(iso).toLocaleDateString()
}

const MIN_W = 340
const MIN_H = 420
const DEFAULT_W = 400
const DEFAULT_H = 600
const TOGGLE_SIZE = 60     // launcher bubble diameter (keep in sync with CSS)
const EDGE_PAD = 8         // min gap between a dragged launcher and the viewport edge
const DRAG_THRESHOLD = 3   // px of movement before a press counts as a drag (not a click)

type ThemeSetting = 'light' | 'dark' | 'system'
type PositionSetting = 'left' | 'right'

interface LauncherPos {
  x: number
  y: number
}

interface Settings {
  theme: ThemeSetting
  position: PositionSetting
  launcher: LauncherPos | null
}

// Per-user UI preferences (theme + dock side + free-dragged launcher spot),
// persisted in localStorage so they survive reloads. Kept client-side only —
// these are presentation knobs, not account settings.
const SETTINGS_KEY = 'netwatch:settings'
const DEFAULT_SETTINGS: Settings = { theme: 'light', position: 'right', launcher: null }

const THEME_OPTIONS: { value: ThemeSetting; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
]
const POSITION_OPTIONS: { value: PositionSetting; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
]

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) }
  } catch {
    /* storage disabled (private mode / strict cookie policy) — use defaults */
  }
  return { ...DEFAULT_SETTINGS }
}

function prefersDark(): boolean {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

export interface Props {
  apiEndpoint: string
  clientId: string
  username?: string
}

type Tab = 'chat' | 'outlook' | 'settings'

interface ThreadSummary {
  id: string
  title?: string | null
  updated_at?: string | null
}

interface SlaSettings {
  exclude_maintenance?: boolean
  exclude_weekends?: boolean
  [key: string]: unknown
}

interface ResizeState {
  startX: number
  startY: number
  startW: number
  startH: number
}

interface DragState {
  startX: number
  startY: number
  originX: number
  originY: number
  moved: boolean
}

export function ChatWidget({ apiEndpoint, clientId, username }: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [size, setSize] = useState<{ w: number; h: number }>({ w: DEFAULT_W, h: DEFAULT_H })
  const [isResizing, setIsResizing] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  // Press-outside-to-close for the history overlay. The header toggle button
  // is excluded so its own onClick handles the close (otherwise the outside
  // press would close and the click would immediately reopen).
  const historyRef = useRef<HTMLDivElement>(null)
  const historyBtnRef = useRef<HTMLButtonElement>(null)
  useOutsidePress([historyRef, historyBtnRef], () => setIsHistoryOpen(false), isHistoryOpen)
  const [olProblems, setOlProblems] = useState<ActiveProblem[]>([])
  const [threads, setThreads] = useState<ThreadSummary[]>([])
  const [threadsLoading, setThreadsLoading] = useState(false)
  const [threadsError, setThreadsError] = useState<string | null>(null)
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('chat')
  const [settings, setSettings] = useState<Settings>(loadSettings)
  const [systemDark, setSystemDark] = useState<boolean>(prefersDark)
  // Live top-left of the launcher when free-dragged; null => use the corner
  // implied by settings.position. Seeded from the persisted spot.
  const [launcherPos, setLauncherPos] = useState<LauncherPos | null>(() => settings.launcher || null)
  const [isDraggingLauncher, setIsDraggingLauncher] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const resizeStateRef = useRef<ResizeState | null>(null)
  const dragRef = useRef<DragState | null>(null)          // in-flight launcher drag bookkeeping
  const latestPosRef = useRef<LauncherPos | null>(null)     // last clamped pos, committed on mouseup
  const suppressClickRef = useRef(false) // swallow the click that ends a drag

  const authHeaders = useMemo<Record<string, string>>(
    () => ({
      'X-Netwatch-Client-Id': clientId,
      'X-Netwatch-Username': username as string,
    }),
    [clientId, username],
  )

  // Per-client SLA settings (header-auth). Lazy-loaded when the Settings tab
  // opens; toggled with an optimistic update + PATCH back.
  const [slaSettings, setSlaSettings] = useState<SlaSettings | null>(null)
  const [slaSaving, setSlaSaving] = useState(false)

  useEffect(() => {
    if (activeTab !== 'settings' || slaSettings) return
    let cancelled = false
    fetch(`${apiEndpoint}/sla/settings`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setSlaSettings(d) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [activeTab, slaSettings, apiEndpoint, authHeaders])

  // Dashboard hand-off target (GET /portal → app_url). Fetched once per open;
  // failure leaves it unset so the next open retries. Without an app_url (env
  // unset / old backend) the Open-in-app button and the "continue in app"
  // popup stay hidden, and Outlook's View-hosts falls back to its in-panel
  // affected-hosts overlay.
  const [appUrl, setAppUrl] = useState<string | null>(null)
  const [portalLoaded, setPortalLoaded] = useState(false)
  // Tenant display name from /portal — the fallback when the embed passes no
  // `username`, matching how the dashboard resolves its greeting name.
  const [portalName, setPortalName] = useState('')
  // The confirm dialog is open for this destination path (null = closed). Set
  // by Outlook's "View hosts", and by a blocked popup on any route so the user
  // still gets a way through.
  const [confirmPath, setConfirmPath] = useState<string | null>(null)
  // Set only when the pre-opened tab was blocked — the dialog then shows a
  // manual link carrying the minted token instead of auto-navigating.
  const [pendingHref, setPendingHref] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || portalLoaded) return
    let cancelled = false
    fetch(`${apiEndpoint}/portal`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return
        setAppUrl(typeof d.app_url === 'string' && d.app_url ? d.app_url.replace(/\/+$/, '') : null)
        if (typeof d.name === 'string' && d.name.trim()) setPortalName(d.name.trim())
        setPortalLoaded(true)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [isOpen, portalLoaded, apiEndpoint, authHeaders])

  // Open the dashboard in a new tab, carrying a one-time login token in the
  // URL *fragment* (never sent to servers) so the user lands signed in. The
  // tab is pre-opened synchronously — popup blockers only trust the live
  // click; an async window.open after the mint would be eaten. Mint failure
  // just lands the tab on the login page. Returns the final URL when the
  // popup WAS blocked so callers with UI can offer a manual link.
  const launchApp = useCallback(async (path: string): Promise<string | null> => {
    if (!appUrl) return null
    const w = window.open('', '_blank')
    if (w) {
      try { w.document.title = 'Opening NetWatch…' } catch { /* ignore */ }
    }
    let url = `${appUrl}${path}`
    try {
      const res = await fetch(`${apiEndpoint}/auth/handoff`, { method: 'POST', headers: authHeaders })
      if (res.ok) {
        const d = await res.json()
        if (d && d.token) url = `${url}#handoff=${encodeURIComponent(d.token)}`
      }
    } catch { /* no token — plain login page */ }
    if (w && !w.closed) {
      w.location = url
      return null
    }
    return url
  }, [appUrl, apiEndpoint, authHeaders])

  // Launch, and if the tab was blocked fall back to the dialog's manual link.
  // Every route goes through here — a blocked popup must never be a dead click.
  const openAppWithHandoff = useCallback(async (path: string) => {
    const blockedUrl = await launchApp(path)
    if (blockedUrl) {
      setConfirmPath(path)
      setPendingHref(blockedUrl)
    } else {
      setConfirmPath(null)
      setPendingHref(null)
    }
  }, [launchApp])

  const openApp = useCallback(() => { void openAppWithHandoff('') }, [openAppWithHandoff])

  const closeConfirm = useCallback(() => {
    setConfirmPath(null)
    setPendingHref(null)
  }, [])

  // A parked handoff token is a live credential and the widget's shadow root is
  // open, so drop the manual link once its 60s window lapses rather than
  // leaving a dead (but readable) token in the DOM.
  useEffect(() => {
    if (!pendingHref) return
    const timer = setTimeout(() => setPendingHref(null), 60_000)
    return () => clearTimeout(timer)
  }, [pendingHref])

  const toggleSla = useCallback(async (field: string) => {
    if (!slaSettings || slaSaving) return
    const value = !slaSettings[field]
    setSlaSettings((s) => ({ ...(s ?? {}), [field]: value }))   // optimistic
    setSlaSaving(true)
    try {
      const res = await fetch(`${apiEndpoint}/sla/settings`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      })
      if (res.ok) setSlaSettings(await res.json())
    } catch {
      /* keep the optimistic value; next open re-fetches */
    } finally {
      setSlaSaving(false)
    }
  }, [slaSettings, slaSaving, apiEndpoint, authHeaders])

  // The composer input is owned here now; chat-core's useChat is transport-
  // agnostic and exposes send()/sendResume() rather than managing input.
  const [input, setInput] = useState('')
  const stream = useMemo(
    () => makeHeaderStream({ api: `${apiEndpoint}/chat/stream`, headers: authHeaders }),
    [apiEndpoint, authHeaders],
  )
  const {
    messages,
    isLoading,
    pendingQuestion,
    send,
    sendResume,
    stop,
    startNewChat: _startNewChat,
    loadThread,
    threadId,
  } = useChat({ stream })

  // Send `text` (composer value or a one-tap suggestion) and clear the box.
  // Mirrors useChat.send's guard so a clear can't outrun a blocked send.
  const submitText = useCallback(
    (text: string) => {
      const t = text.trim()
      if (!t || isLoading || pendingQuestion) return
      setInput('')
      send(t)
    },
    [send, isLoading, pendingQuestion],
  )
  // Wrap startNewChat so we tell the backend the previous thread is
  // done. The backend then synthesises any durable facts the user said
  // into their per-user memory before LangGraph forgets the thread.
  // Fire-and-forget — the widget doesn't wait for it.
  const startNewChat = useCallback(() => {
    if (threadId) {
      fetch(`${apiEndpoint}/chat/threads/${threadId}/close`, {
        method: 'POST',
        headers: authHeaders,
      }).catch(() => {})
    }
    _startNewChat()
  }, [threadId, apiEndpoint, authHeaders, _startNewChat])

  // Outlook → chat handoff: a seeded ask from another tab always lands in a
  // FRESH conversation, never appended to whatever thread was open. The text is
  // staged in a ref rather than sent inline: startNewChat aborts the stream but
  // isLoading/pendingQuestion only clear on the next render, so an inline
  // submitText would be swallowed by its own guard. The effect below sends once
  // the reset has rendered. An empty `t` just switches tabs (no wipe, no send).
  const pendingSeedRef = useRef<string | null>(null)
  const askInNewChat = useCallback(
    (t: string) => {
      if (t.trim()) {
        startNewChat()
        pendingSeedRef.current = t
      }
      setActiveTab('chat')
    },
    [startNewChat],
  )
  // No dep array on purpose: askInNewChat always causes a render (tab switch /
  // chat reset), and with deps this wouldn't re-fire when the chat was already
  // idle (nothing in the deps changes). The ref guard makes re-runs free.
  useEffect(() => {
    const t = pendingSeedRef.current
    if (t && !isLoading && !pendingQuestion) {
      pendingSeedRef.current = null
      submitText(t)
    }
  })

  const confidence = useConfidence({
    apiEndpoint,
    headers: authHeaders,
    enabled: isOpen,   // refresh the env score while the panel is open
  })

  // Saved questions (the FAQ panel + Pinned chips above the composer). Loaded
  // once the panel opens; every edit refetches.
  const faq = useFaq({ apiEndpoint, headers: authHeaders, enabled: isOpen })

  const pinnedFaq = useMemo(() => pinnedEntries(faq.entries), [faq.entries])
  const [faqOpen, setFaqOpen] = useState(false)
  // Press-outside-to-close. The toggle button is excluded so its own onClick
  // handles closing — otherwise the outside-press closes and the click
  // immediately reopens.
  const faqPanelRef = useRef<HTMLDivElement>(null)
  const faqBtnRef = useRef<HTMLButtonElement>(null)
  useOutsidePress([faqPanelRef, faqBtnRef], () => setFaqOpen(false), faqOpen)

  // The shared <Outlook> derives the hero severity, today's strip cell and
  // the overdue pill from the live problems list — poll it every minute
  // while the Outlook tab is open so the view tracks the monitor closely.
  useEffect(() => {
    if (!isOpen || activeTab !== 'outlook') return
    let alive = true
    const load = () => {
      confidence.getProblems()
        .then((p) => { if (alive) setOlProblems(p as ActiveProblem[]) })
        .catch(() => { /* leave the last list */ })
    }
    load()
    const timer = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(timer) }
    // confidence.getProblems is a stable useCallback (keyed on apiEndpoint).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab])

  const fetchThreads = async () => {
    setThreadsLoading(true)
    setThreadsError(null)
    try {
      const res = await fetch(`${apiEndpoint}/chat/threads`, { headers: authHeaders })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setThreads(data.items || [])
    } catch (err) {
      setThreadsError((err as Error).message || 'Failed to load history')
    } finally {
      setThreadsLoading(false)
    }
  }

  const openHistory = () => {
    setIsHistoryOpen(true)
    fetchThreads()
  }

  const onPickThread = async (thread: ThreadSummary) => {
    setLoadingThreadId(thread.id)
    setThreadsError(null)
    try {
      const res = await fetch(`${apiEndpoint}/chat/threads/${thread.id}`, { headers: authHeaders })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      loadThread(thread.id, data.messages || [])
      setIsHistoryOpen(false)
    } catch (err) {
      setThreadsError((err as Error).message || 'Failed to load thread')
    } finally {
      setLoadingThreadId(null)
    }
  }

  const onNewChat = () => {
    startNewChat()
    setIsHistoryOpen(false)
    setHelloSeq((n) => n + 1)
  }

  // Persist UI settings (theme + dock side) across sessions.
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings))
    } catch {
      /* ignore — storage may be unavailable */
    }
  }, [settings])

  // Track OS appearance so the "System" theme follows it live.
  useEffect(() => {
    if (!window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])

  const effectiveTheme =
    settings.theme === 'system' ? (systemDark ? 'dark' : 'light') : settings.theme

  // --- Launcher placement & panel open-direction ------------------------
  // When the launcher has been free-dragged we anchor to its coordinates and
  // open the panel toward screen-center so it stays on screen. Otherwise we
  // fall back to the docked corner from settings.position.
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768
  let anchorRight = settings.position !== 'left'
  let openUp = true
  if (launcherPos) {
    anchorRight = launcherPos.x + TOGGLE_SIZE / 2 > vw / 2
    openUp = launcherPos.y + TOGGLE_SIZE / 2 > vh / 2
  }
  // The resize grip sits on the panel corner opposite its anchor; when that
  // lands on the top-right header controls, mirror the header out of its way.
  const mirrorHeader = openUp && !anchorRight

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, isLoading])

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [isOpen])

  useEffect(() => {
    if (!isResizing) return

    const onMove = (e: MouseEvent) => {
      const s = resizeStateRef.current
      if (!s) return
      // Grip sits on the corner opposite the panel's anchor, so the drag
      // direction that *grows* the panel flips with anchor side / open dir.
      const dx = anchorRight ? s.startX - e.clientX : e.clientX - s.startX
      const dy = openUp ? s.startY - e.clientY : e.clientY - s.startY
      const maxW = Math.max(MIN_W, window.innerWidth - 48)
      const maxH = Math.max(MIN_H, window.innerHeight - 120)
      const w = Math.min(maxW, Math.max(MIN_W, s.startW + dx))
      const h = Math.min(maxH, Math.max(MIN_H, s.startH + dy))
      setSize({ w, h })
    }
    const onUp = () => {
      setIsResizing(false)
      resizeStateRef.current = null
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [isResizing, anchorRight, openUp])

  const startResize = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    resizeStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: size.w,
      startH: size.h,
    }
    setIsResizing(true)
  }

  // Drag the launcher bubble anywhere; commit + persist on release. A press
  // that never crosses DRAG_THRESHOLD is left as a plain click (open/close).
  useEffect(() => {
    if (!isDraggingLauncher) return
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      if (Math.abs(e.clientX - d.startX) > DRAG_THRESHOLD ||
          Math.abs(e.clientY - d.startY) > DRAG_THRESHOLD) {
        d.moved = true
      }
      const maxX = window.innerWidth - TOGGLE_SIZE - EDGE_PAD
      const maxY = window.innerHeight - TOGGLE_SIZE - EDGE_PAD
      const pos = {
        x: Math.min(maxX, Math.max(EDGE_PAD, d.originX + (e.clientX - d.startX))),
        y: Math.min(maxY, Math.max(EDGE_PAD, d.originY + (e.clientY - d.startY))),
      }
      latestPosRef.current = pos
      setLauncherPos(pos)
    }
    const onUp = () => {
      const d = dragRef.current
      setIsDraggingLauncher(false)
      dragRef.current = null
      if (d && d.moved) {
        suppressClickRef.current = true   // swallow the click this drag ends with
        if (latestPosRef.current) {
          setSettings((s) => ({ ...s, launcher: latestPosRef.current }))
        }
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [isDraggingLauncher])

  // Keep a free-dragged launcher inside the viewport across window resizes.
  useEffect(() => {
    if (!launcherPos) return
    const onResize = () => {
      setLauncherPos((p) => {
        if (!p) return p
        const maxX = window.innerWidth - TOGGLE_SIZE - EDGE_PAD
        const maxY = window.innerHeight - TOGGLE_SIZE - EDGE_PAD
        return {
          x: Math.min(maxX, Math.max(EDGE_PAD, p.x)),
          y: Math.min(maxY, Math.max(EDGE_PAD, p.y)),
        }
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [launcherPos])

  const startLauncherDrag = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    suppressClickRef.current = false
    const rect = e.currentTarget.getBoundingClientRect()
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: rect.left,
      originY: rect.top,
      moved: false,
    }
    latestPosRef.current = null
    setIsDraggingLauncher(true)
  }

  const showEmpty = messages.length === 0 && !isLoading
  // The embed's `username` handoff falls back to the literal 'user' when the
  // host has no real name to pass — a placeholder, not a name, so the tenant's
  // display name reads better than greeting someone as "User".
  const greetingName = useMemo(() => {
    const u = username?.trim()
    const name = u && u.toLowerCase() !== 'user' ? u : portalName
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : ''
  }, [username, portalName])
  // Bumped on every "New chat" so each empty screen draws a fresh variant from
  // the server's pool instead of pinning the first one for the whole session.
  const [helloSeq, setHelloSeq] = useState(0)
  const hello = useSalutation({
    apiEndpoint,
    headers: authHeaders,
    name: greetingName,
    enabled: isOpen && showEmpty,
    seq: helloSeq,
  })
  const lastMsg = messages[messages.length - 1]
  const showTyping = isLoading && (!lastMsg || lastMsg.role !== 'assistant' || !lastMsg.content)

  // Free-dragged launcher pins the root by top-left; otherwise the CSS corner
  // (nw-pos-*) applies. Panel + grip placement derive from anchor/open dir.
  const rootStyle: React.CSSProperties | undefined = launcherPos
    ? { left: `${launcherPos.x}px`, top: `${launcherPos.y}px`, right: 'auto', bottom: 'auto' }
    : undefined
  const panelStyle: React.CSSProperties = {
    width: `${size.w}px`,
    height: `${size.h}px`,
    left: anchorRight ? 'auto' : 0,
    right: anchorRight ? 0 : 'auto',
    top: openUp ? 'auto' : '76px',
    bottom: openUp ? '76px' : 'auto',
    transformOrigin: `${openUp ? 'bottom' : 'top'} ${anchorRight ? 'right' : 'left'}`,
  }
  const handleStyle: React.CSSProperties = {
    left: anchorRight ? 0 : 'auto',
    right: anchorRight ? 'auto' : 0,
    top: openUp ? 0 : 'auto',
    bottom: openUp ? 'auto' : 0,
    cursor: anchorRight === openUp ? 'nwse-resize' : 'nesw-resize',
  }
  const handleFlip = `scale(${anchorRight ? 1 : -1}, ${openUp ? 1 : -1})`

  return (
    <div
      className={`nw-root nw-pos-${settings.position} nw-theme-${effectiveTheme}${mirrorHeader ? ' nw-mirror' : ''}`}
      style={rootStyle}
    >
      {isOpen && (
        <div
          className={`nw-panel ${isResizing ? 'resizing' : ''}`}
          role="dialog"
          aria-label="Netwatch assistant"
          style={panelStyle}
        >
          <div
            className="nw-resize"
            onMouseDown={startResize}
            title="Drag to resize"
            aria-label="Resize"
            style={handleStyle}
          >
            <span style={{ display: 'inline-flex', transform: handleFlip }}>
              <IconResize />
            </span>
          </div>
          <div className="nw-header">
            <div className="nw-avatar"><IconLogo /></div>
            <div className="nw-header-text">
              <div className="nw-header-title">Netwatch</div>
              <div className="nw-header-subtitle">AI Monitoring Assistant</div>
            </div>
            {activeTab === 'chat' && (
              <>
                <button
                  className="nw-close"
                  onClick={onNewChat}
                  aria-label="New chat"
                  title="New chat"
                >
                  <IconPlus />
                </button>
                <button
                  ref={historyBtnRef}
                  className="nw-close"
                  onClick={isHistoryOpen ? () => setIsHistoryOpen(false) : openHistory}
                  aria-label="Chat history"
                  title="Chat history"
                >
                  <IconHistory />
                </button>
              </>
            )}
            <button className="nw-close" onClick={() => setIsOpen(false)} aria-label="Close">
              <IconClose />
            </button>
          </div>

          <div className="nw-tabs" role="tablist">
            <button
              className={`nw-tab ${activeTab === 'chat' ? 'is-active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'chat'}
              onClick={() => setActiveTab('chat')}
            >
              Chat
            </button>
            <button
              className={`nw-tab ${activeTab === 'outlook' ? 'is-active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'outlook'}
              onClick={() => {
                setActiveTab('outlook')
                setIsHistoryOpen(false)
                confidence.refresh(true)
              }}
            >
              Outlook
            </button>
            <button
              className={`nw-tab ${activeTab === 'settings' ? 'is-active' : ''}`}
              role="tab"
              aria-selected={activeTab === 'settings'}
              onClick={() => {
                setActiveTab('settings')
                setIsHistoryOpen(false)
              }}
            >
              Settings
            </button>
            {appUrl && (
              <button
                className="nw-open-app"
                onClick={() => openApp()}
                title="Open the NetWatch dashboard in a new tab"
                aria-label="Open the NetWatch dashboard in a new tab"
              >
                <IconExternal />
                <span className="nw-open-app-label">Open in app</span>
              </button>
            )}
          </div>

          {isHistoryOpen && (
            <div ref={historyRef} className="nw-history" role="dialog" aria-label="Conversation history">
              <div className="nw-history-header">
                <div className="nw-history-title">Previous conversations</div>
                <button
                  className="nw-history-new"
                  onClick={onNewChat}
                  title="Start a new chat"
                >
                  <IconPlus /> New
                </button>
              </div>
              <div className="nw-history-body">
                {threadsLoading && <div className="nw-history-empty">Loading…</div>}
                {!threadsLoading && threadsError && (
                  <div className="nw-history-empty">⚠️ {threadsError}</div>
                )}
                {!threadsLoading && !threadsError && threads.length === 0 && (
                  <div className="nw-history-empty">No conversations yet.</div>
                )}
                {!threadsLoading && !threadsError && threads.map((t) => (
                  <button
                    key={t.id}
                    className="nw-history-item"
                    onClick={() => onPickThread(t)}
                    disabled={loadingThreadId === t.id}
                  >
                    <div className="nw-history-item-title">
                      {t.title || 'Untitled conversation'}
                    </div>
                    <div className="nw-history-item-meta">
                      {formatRelativeTime(t.updated_at)}
                      {loadingThreadId === t.id && ' · loading…'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'chat' ? (
          <>
          <div className="nw-messages" ref={scrollRef}>
            {showEmpty ? (
              <div className="nw-empty">
                <Salutation text={hello} logo={<IconLogo />} />
              </div>
            ) : (
              <>
                {messages.map((m: ChatMessage, i: number) => {
                  if (m.role === 'question') {
                    const isLatest =
                      pendingQuestion && pendingQuestion.id === m.question.id
                    return (
                      <ClarifyCard
                        key={i}
                        question={m.question}
                        onAnswer={sendResume}
                        disabled={!isLatest}
                      />
                    )
                  }
                  const isAssistant = m.role === 'assistant'
                  const isLastAssistant =
                    isAssistant && i === messages.length - 1
                  const stillBusy = isLastAssistant && isLoading
                  return (
                    <div key={i} className={`nw-msg ${m.role}`}>
                      <div className="nw-msg-stack">
                        {stillBusy && (
                          <div className="nw-status" role="status" aria-live="polite">
                            <span className="nw-status-spinner" />
                            <span className="nw-status-label">
                              <ThinkingLabel />
                            </span>
                          </div>
                        )}
                        {isAssistant && ((m.thinking?.length ?? 0) > 0 || m.reasoning) && (
                          <ThinkingBlock
                            entries={m.thinking}
                            reasoning={m.reasoning}
                            busy={stillBusy}
                          />
                        )}
                        {(m.content || !isAssistant) && (
                          <div className="nw-bubble">
                            {isAssistant ? (
                              <AssistantContent content={m.content} busy={stillBusy} />
                            ) : (
                              m.content
                            )}
                          </div>
                        )}
                        {isAssistant && (m.downloads ?? (m.download ? [m.download] : [])).map((f, fi) => (
                          <DownloadCard key={`${f.url ?? f.filename ?? fi}`} {...f} apiEndpoint={apiEndpoint} />
                        ))}
                      </div>
                    </div>
                  )
                })}
                {showTyping && (
                  <div className="nw-msg assistant">
                    <div className="nw-bubble" style={{ padding: 0 }}>
                      <div className="nw-typing">
                        <span /><span /><span />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Saved questions: the slide-up panel + the Pinned chips row, docked
              right above the input — the same pair the dashboard's chat page
              renders, over the same per-user store. */}
          <div className="nw-faq-dock">
            <FaqPanel
              open={faqOpen}
              rootRef={faqPanelRef}
              entries={faq.entries}
              loadFailed={faq.loadFailed}
              onRetry={faq.refresh}
              starCap={faq.starCap}
              pinnedCount={pinnedFaq.length}
              chatBusy={isLoading}
              mutating={faq.mutating}
              error={faq.error}
              onAsk={(q) => { setFaqOpen(false); submitText(q) }}
              onAdd={faq.add}
              onStar={faq.star}
              onDelete={faq.remove}
            />
            <FaqBar
              pinned={pinnedFaq}
              buttonRef={faqBtnRef}
              onToggle={() => setFaqOpen((v) => !v)}
              onAsk={submitText}
              busy={isLoading}
            />
          </div>

          <div className="nw-input-wrap">
            <Composer
              inputRef={inputRef}
              value={input}
              onChange={setInput}
              onSubmit={() => submitText(input)}
              placeholder={pendingQuestion ? 'Answer the question above…' : 'Ask anything…'}
              disabled={!!pendingQuestion}
              busy={isLoading}
              onStop={stop}
              footer="Powered by Netwatch AI"
            />
          </div>
          </>
          ) : activeTab === 'outlook' ? (
            <div className="nw-messages nw-outlook-scroll">
              <Outlook
                confidence={confidence.data}
                history={confidence.history}
                problems={olProblems}
                disks={confidence.disks}
                insights={confidence.insights}
                error={confidence.error}
                getToday={confidence.getToday}
                getDay={confidence.getDay}
                getDiskTrend={confidence.getDiskTrend}
                onAsk={askInNewChat}
                onAskNew={askInNewChat}
                insightAskEnabled={INSIGHT_ASK_ENABLED}
                onViewHosts={appUrl ? () => setConfirmPath('/incidents') : undefined}
                onRetry={() => confidence.refresh(true)}
              />
            </div>
          ) : (
            <div className="nw-settings">
              <div className="nw-settings-section">
                <div className="nw-settings-label">Theme</div>
                <div className="nw-segment" role="group" aria-label="Theme">
                  {THEME_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`nw-segment-btn ${settings.theme === opt.value ? 'is-active' : ''}`}
                      aria-pressed={settings.theme === opt.value}
                      onClick={() => setSettings((s) => ({ ...s, theme: opt.value }))}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="nw-settings-hint">
                  {settings.theme === 'system'
                    ? 'Matches your operating system’s light or dark appearance.'
                    : 'Pick how the assistant looks.'}
                </div>
              </div>

              <div className="nw-settings-section">
                <div className="nw-settings-label">Position</div>
                <div className="nw-segment" role="group" aria-label="Position">
                  {POSITION_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`nw-segment-btn ${!launcherPos && settings.position === opt.value ? 'is-active' : ''}`}
                      aria-pressed={!launcherPos && settings.position === opt.value}
                      onClick={() => {
                        // Picking a corner also clears any free-dragged spot.
                        setLauncherPos(null)
                        setSettings((s) => ({ ...s, position: opt.value, launcher: null }))
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="nw-settings-hint">
                  {launcherPos
                    ? 'The launcher has been dragged to a custom spot. Pick Left or Right to snap it back to a corner.'
                    : `Docks the chat window and the launcher button to the ${settings.position} side. Tip: you can also drag the button anywhere.`}
                </div>
              </div>

              <div className="nw-settings-section">
                <div className="nw-settings-label">SLA exclusions</div>
                <div className="nw-toggle-card">
                  <div className="nw-toggle-item">
                    <div className="nw-toggle-info">
                      <div className="nw-toggle-title">Ignore hosts on maintenance</div>
                      <div className="nw-toggle-desc">Leave planned-maintenance time out of the SLA.</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!(slaSettings && slaSettings.exclude_maintenance)}
                      aria-label="Ignore hosts on maintenance"
                      className={`nw-switch ${slaSettings && slaSettings.exclude_maintenance ? 'is-on' : ''}`}
                      disabled={!slaSettings || slaSaving}
                      onClick={() => toggleSla('exclude_maintenance')}
                    >
                      <span className="nw-switch-knob" />
                    </button>
                  </div>
                  <div className="nw-toggle-item">
                    <div className="nw-toggle-info">
                      <div className="nw-toggle-title">Ignore hosts on weekend</div>
                      <div className="nw-toggle-desc">Exclude Saturday &amp; Sunday hours from the SLA.</div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!(slaSettings && slaSettings.exclude_weekends)}
                      aria-label="Ignore hosts on weekend"
                      className={`nw-switch ${slaSettings && slaSettings.exclude_weekends ? 'is-on' : ''}`}
                      disabled={!slaSettings || slaSaving}
                      onClick={() => toggleSla('exclude_weekends')}
                    >
                      <span className="nw-switch-knob" />
                    </button>
                  </div>
                </div>
                <div className="nw-settings-hint">
                  {slaSettings
                    ? 'Excluded time is left out of every host’s SLA — applied at the next recompute (every 6h).'
                    : 'Loading…'}
                </div>
              </div>
            </div>
          )}
          {confirmPath !== null && (
            <ConfirmDialog
              title={pendingHref ? 'Popup blocked' : 'Continue in app?'}
              message={
                pendingHref
                  ? 'Your browser blocked the new tab. Use the link below to continue — it expires in a minute.'
                  : confirmPath === '/incidents'
                    ? 'Affected hosts open in the NetWatch dashboard. Open it in a new tab?'
                    : 'The NetWatch dashboard opens in a new tab. Continue?'
              }
              confirmLabel="Open app"
              fallbackHref={pendingHref}
              onConfirm={() => openAppWithHandoff(confirmPath)}
              onCancel={closeConfirm}
            />
          )}
        </div>
      )}

      <button
        className={`nw-toggle ${isOpen ? 'is-open' : ''}`}
        onMouseDown={startLauncherDrag}
        onClick={() => {
          // A press that turned into a drag ends in a click we must ignore.
          if (suppressClickRef.current) {
            suppressClickRef.current = false
            return
          }
          setIsOpen((o) => !o)
        }}
        style={{ cursor: isDraggingLauncher ? 'grabbing' : 'grab' }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        title="Click to open · drag to move"
      >
        {isOpen ? <IconChevronDown /> : <IconLogo />}
      </button>
    </div>
  )
}
